import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSForm,
  WSLoader,
  WSSelect
} from "@wingspanhq/fe-component-library";
import {
  ListLedgerAccountsFilterParameterClassificationEq,
  ReferenceDataRefreshRequestCollectionsInner
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  queriesAccountingConnections,
  queriesAccountingReferenceData,
  queriesPayeeEngagements,
  queriesPayees
} from "@query";
import { selectorAccountingVendorOptions } from "../../../Integrations/selectors/selectorAccountingVendorOptions";
import { selectorLedgerAccountOptions } from "../../../Integrations/selectors/selectorLedgerAccountOptions";
import { selectorQuickbooksConnection } from "../../../Integrations/selectors/selectorQuickbooksConnection";
import { useXWingspanPayerId } from "../../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";

type Props = {
  payeeId: string;
  engagementId: string | undefined;
  vendorId: string | undefined;
  expenseAccountId: string | undefined;
  onClose: () => void;
};

type FormData = {
  qboVendorId?: string;
  qboExpenseAccountId?: string;
};

const EditPayeeIntegrationModal: React.FC<Props> = props => {
  const { openSnackbar } = useWSSnackbar();
  const xWingspanAccount = useXWingspanPayerId();

  const queryConnections =
    queriesAccountingConnections.listAccountingConnections.useQuery({});
  const connection = selectorQuickbooksConnection(queryConnections.data);
  const connectionId = connection?.id;

  const queryVendors =
    queriesAccountingReferenceData.listAccountingVendors.useQueryAll(
      { connectionId: connectionId as string },
      { enabled: !!connectionId }
    );
  // v1 had a dedicated expense-accounts endpoint; v3 serves one ledger-account
  // collection narrowed by classification.
  const queryExpenseAccounts =
    queriesAccountingReferenceData.listLedgerAccounts.useQueryAll(
      {
        connectionId: connectionId as string,
        filter: {
          classification: {
            eq: ListLedgerAccountsFilterParameterClassificationEq.EXPENSE
          }
        }
      },
      { enabled: !!connectionId }
    );

  // The mapping lives on the assignment when one is in context and on the payee
  // itself otherwise — both are first-class v3 fields (`PayeeEngagementUpdate` /
  // `PayeeUpdate`), so both branches write directly.
  const [updatePayee, updatePayeeMeta] =
    queriesPayees.updatePayee.useMutation();
  const [updateEngagement, updateEngagementMeta] =
    queriesPayeeEngagements.updatePayeeEngagement.useMutation();

  // v3 replacement for the v1 resync modal: the refresh is an async operation, so
  // it is fire-and-report rather than something to await a fresh list on.
  const [refreshReferenceData] =
    queriesAccountingReferenceData.refreshReferenceData.useMutation();

  const handleResync = (
    collection: ReferenceDataRefreshRequestCollectionsInner,
    label: string
  ) => {
    if (!connectionId) {
      return;
    }

    refreshReferenceData(
      {
        connectionId,
        referenceDataRefreshRequest: { collections: [collection] }
      },
      {
        onSuccess: () => {
          openSnackbar({
            type: "success",
            duration: 5000,
            message: `Refreshing ${label} from QuickBooks. This can take a moment.`
          });
        },
        onError: () => {
          openSnackbar({
            type: "warning",
            duration: 5000,
            message: `Failed to refresh ${label}`
          });
        }
      }
    );
  };

  if (
    queryConnections.isLoading ||
    queryVendors.isLoading ||
    queryExpenseAccounts.isLoading
  ) {
    return <WSLoader.Spinner size="M" />;
  }

  const vendorOptions = selectorAccountingVendorOptions(queryVendors.flatData);
  const accountOptions = selectorLedgerAccountOptions(
    queryExpenseAccounts.flatData
  );
  const defaultAccount = (queryExpenseAccounts.flatData ?? []).find(
    account =>
      account.id === connection?.mappingDefaults?.expenseLedgerAccountId
  );

  const handleSubmit = (values: FormData) => {
    const onSuccess = () => {
      props.onClose();
    };

    // A cleared select un-maps, which the API expresses as an explicit `null`;
    // `undefined` would be omitted from the PATCH and silently keep the old
    // mapping. Both update models type these as `string`, so widen once here —
    // the same boundary cast `EditPayerIntegrationModal` makes.
    const mapping = {
      quickbooksVendorId: values.qboVendorId || null,
      quickbooksExpenseAccountId: values.qboExpenseAccountId || null
    } as unknown as {
      quickbooksVendorId?: string;
      quickbooksExpenseAccountId?: string;
    };

    if (props.engagementId) {
      // v1 nested this under `payerOwnedData.integration.quickbooks`; v3 flattens
      // it onto the engagement update body.
      updateEngagement(
        {
          payeeId: props.payeeId,
          payeeEngagementId: props.engagementId,
          payeeEngagementUpdate: mapping,
          xWingspanAccount
        },
        { onSuccess }
      );
      return;
    }

    updatePayee(
      { payeeId: props.payeeId, payeeUpdate: mapping, xWingspanAccount },
      { onSuccess }
    );
  };

  const meta = props.engagementId ? updateEngagementMeta : updatePayeeMeta;

  return (
    <WSForm<FormData>
      defaultValues={{
        qboVendorId: props.vendorId,
        qboExpenseAccountId: props.expenseAccountId
      }}
      onSubmit={handleSubmit}
    >
      <WSForm.Field
        mb="XL"
        name="qboVendorId"
        component={WSSelect}
        componentProps={{
          internalSearch: true,
          placeholder: "Not mapped, create new Vendor",
          options: vendorOptions,
          action: {
            text: "Resync vendors",
            onClick: () =>
              handleResync(
                ReferenceDataRefreshRequestCollectionsInner.VENDORS,
                "vendors"
              )
          }
        }}
        label="QBO Vendor"
      />
      <WSForm.Field
        mb="2XL"
        name="qboExpenseAccountId"
        component={WSSelect}
        componentProps={{
          internalSearch: true,
          options: accountOptions,
          placeholder: defaultAccount
            ? `Use default "${defaultAccount.displayName}"`
            : "Not mapped",
          action: {
            text: "Resync accounts",
            onClick: () =>
              handleResync(
                ReferenceDataRefreshRequestCollectionsInner.LEDGER_ACCOUNTS,
                "expense accounts"
              )
          }
        }}
        label="Default QBO Expense Account"
      />

      <ErrorAlert
        mb="XL"
        error={meta.error}
        context="EditCollaboratorIntegrations"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Update",
            kind: "Primary",
            type: "submit",
            loading: meta.isLoading
          },
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: props.onClose
          }
        ]}
      />
    </WSForm>
  );
};

export const useEditPayeeIntegrationModal = () =>
  useWSModal(EditPayeeIntegrationModal, {
    title: "Edit integration",
    size: "S"
  });
